import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import Icon from "../../../../stories/Foundation/Icons";
import { IconNames } from "../../../../stories/Foundation/Icons";
import IconLogo from "../../../Icon/IconLogo";
import IconSearch from "../../../Icon/IconSearch";
import IconClose from "../../../Icon/IconClose";
import LinkWrap from "../../../LinkWrap/component";
import MenuNav from "../MenuNav/component";
import SearchBar from "../../../SearchBar/component";
import { trackToggleButtonClick } from "../../../../actions/Tracking/utils";
import ProfileButtonNav from "../ProfileButtonNav/component";
import classNames from "classnames";
import useIsClientHydrated from "../../../../hooks/useIsClientHydrated";

export const PrimaryNav = ({
    setMenuOpen,
    menuOpen,
    viewport = "",
    isFallback = false,
}) => {
    const isClientHydrated = useIsClientHydrated();
    const path = useSelector((state) => state.app.path);
    const subSection = useSelector((state) => state.app.subSection);
    const eventLink = useSelector(
        (state) => state.featureflags.globalEventLink
    );
    const logoRef = useRef(null);
    const closeMenuRef = useRef(null);
    const menuButtonRef = useRef(null);
    const toggleNavVisibilityCacheBreaker = useRef(null);

    const [active, setActive] = useState(false);
    useEffect(() => {
        setMenuOpen(false);
        setActive(false);
    }, [path]);

    /**
     * For Accessibility Purposes
     * Spacebar will open/close menu/search and ESC will hide them
     * @param {string} type Type of Event Handler to toggle
     * @param {object} e Event Object
     * @returns {void}
     */
    const keyPressHandler = (type, e) => {
        const spaceKey = 32;
        const escKey = 27;

        // Toggle Menu and Search with spacebar
        // keyCode is going to be deprecated, thus using it as fallback only
        if (e.which === spaceKey || e.keyCode === spaceKey) {
            type === "menu" && toggleNavVisibility(e);
            type === "search" && toggleSearchVisibility(e);
        }

        // Close Menu and Search with ESC only when it's opened
        // keyCode is going to be deprecated, thus using it as fallback only
        if (e.key === "Escape" || e.key === "Esc" || e.keyCode === escKey) {
            type === "menu" && menuOpen && toggleNavVisibility(e);
            type === "search" && active && toggleSearchVisibility(e);
        }
    };
    const keyPressHandlerDialog = (e) => {
        const tabKey = 9;
        const escKey = 27;

        const logoNav = logoRef.current;
        const lastCloseMenu = closeMenuRef.current;

        if (e.which === tabKey || e.keyCode === tabKey) {
            if (document.activeElement === lastCloseMenu) {
                logoNav.focus();
            }

            if (e.shiftKey) {
                if (document.activeElement === logoNav) {
                    lastCloseMenu.focus();
                }
            }
        }

        if (
            (e.key === "Escape" || e.key === "Esc" || e.keyCode === escKey) &&
            menuOpen
        ) {
            toggleNavVisibility(e);
        }
    };

    /**
     * Toggles the visibility of the menu, open or closed navigation links
     * updates aria properties to reflect behaviour and assists with focus control to meet expectations regardless of user input type (keyboard/mouse/assistive technology)
     * @return {void}
     * @param {object} e The mouse event
     */
    const toggleNavVisibility = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleNavVisibilityCacheBreaker.current();
    };

    toggleNavVisibilityCacheBreaker.current = () => {
        trackToggleButtonClick(`global nav menu`, menuOpen); // Amplitute tracking
        setMenuOpen(!menuOpen);

        if (menuButtonRef?.current?.focus && menuOpen) {
            menuButtonRef.current.focus();
        }
    };

    /**
     * Toggles the visibility of the search bar
     * @param {object} event The click event
     * @return {void}
     */
    const toggleSearchVisibility = (event) => {
        event.preventDefault();
        setActive((prev) => !prev);
    };
    /**
     * Render the CBC gem logo
     * @returns {object} The JSX for the Logo
     */
    const renderLogo = () => {
        const logoProps = {
            className: "headerLogo globalHeaderLogo",
        };

        const logoLinkProps = {
            className: "logo",
            external: isFallback,
            url: "/",
        };

        return (
            <div className="logoWrapper">
                <LinkWrap
                    {...logoLinkProps}
                    ref={logoRef}
                    aria-label="CBC Home Page"
                >
                    <IconLogo {...logoProps} />
                </LinkWrap>
            </div>
        );
    };

    const renderSearchButton = () => {
        const searchIconProps = {
            "aria-expanded": active ? "true" : "false",
            "aria-label": "Search",
            className: "navLink search",
            onClick: toggleSearchVisibility,
            external: false,
            url: "/search",
            onKeyDown: (e) => keyPressHandler("search", e),
            id: "searchButton",
            role: "button",
            "data-testid": "navSearchButton",
        };

        return (
            <LinkWrap {...searchIconProps}>
                <IconSearch className="headerSearch" />
                <span
                    className="searchText"
                    data-cy={isClientHydrated ? "search-text" : undefined}
                >
                    Search
                </span>
            </LinkWrap>
        );
    };
    const navProps = {
        subSection,
        path,
        menuCallback: toggleNavVisibility,
        searchCallback: toggleSearchVisibility,
        menuOpen,
        viewport,
    };

    const menuLinkProps = {
        className: "menuButton",
        external: false,
        ref: menuButtonRef,
        onClick: toggleNavVisibility,
        onKeyDown: (e) => keyPressHandler("menu", e),
        id: "menuButton",
        role: "button",
    };

    const closeButtonClasses = classNames("searchClose", {
        active,
    });

    const headerSearchBarClasses = classNames("headerSearchBar", {
        active,
    });
    return (
        /*eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
        <div
            className="menuNavWrapper primaryNav"
            role="dialog"
            aria-modal={menuOpen}
            aria-label="CBC main navigation"
            data-cy="primaryNavbar"
            onKeyDown={keyPressHandlerDialog}
        >
            <button
                className="toggleMenu"
                onClick={toggleNavVisibility}
                tabIndex="-1"
                aria-label="close menu"
                aria-hidden="true"
            />

            <div className="menuButtonWrapper">
                <LinkWrap
                    url="//www.cbc.ca/sitemap"
                    {...menuLinkProps}
                    data-testid="menuButton"
                    data-cy={isClientHydrated ? "menuButton" : undefined}
                    aria-label={menuOpen ? "Close Menu" : "Menu"}
                >
                    {menuOpen ? (
                        <Icon
                            name={IconNames.Close}
                            size="26"
                            ariaHidden={true}
                        />
                    ) : (
                        <Icon
                            name={IconNames.Hamburger}
                            size="22"
                            ariaHidden={true}
                        />
                    )}
                    <span className="menuText">
                        {menuOpen ? "Close Menu" : "Menu"}
                    </span>
                </LinkWrap>
            </div>

            {renderLogo()}

            {eventLink ? (
                <EventLink {...createGobalLinkProps(eventLink, !menuOpen)} />
            ) : null}

            <div className="sideMenu">
                <div className={headerSearchBarClasses}>
                    <SearchBar type="compact" active={active} />
                    <button
                        className={closeButtonClasses}
                        onClick={toggleSearchVisibility}
                    >
                        <svg
                            fill="#ffffff"
                            width="20px"
                            height="19px"
                            viewBox="0 0 20 19"
                            focusable="false"
                            aria-label="close"
                        >
                            <polygon points="20 0.01 17.6 0 10 7.6 2.4 0 0 0.01 8.79 8.81 0 17.6 2.44 17.57 10 10.02 17.56 17.57 20 17.6 11.21 8.81 20 0.01"></polygon>
                        </svg>
                    </button>
                </div>
                {renderSearchButton()}
                <ProfileButtonNav />
            </div>

            <MenuNav {...navProps} />
            <button
                className="menuClose sclt-menuRoundIconClose"
                onClick={toggleNavVisibility}
                aria-label="Close Menu"
                ref={closeMenuRef}
            >
                <IconClose />
            </button>
        </div>
    );
};

const EventLink = ({ url, largeText, smallText, display }) => {
    if (!url) {
        return null;
    }
    if (!largeText) {
        return null;
    }
    if (!smallText) {
        return null;
    }
    if (!display) {
        return null;
    }
    return (
        <LinkWrap
            url={url}
            className="menuButton globalEventLink"
            data-feature-name="navigation"
            data-feature-instance="global-nav"
        >
            <span className="globalEventLinkLarge">{largeText}</span>
            <span className="globalEventLinkSmall">{smallText}</span>
        </LinkWrap>
    );
};

const createGobalLinkProps = (flag, display) => {
    if (!flag) {
        return null;
    }
    const [smallText, largeText, url] = flag.split("||");
    return {
        url,
        smallText,
        largeText,
        display,
    };
};

PrimaryNav.propTypes = {
    viewport: PropTypes.string,
    isFallback: PropTypes.bool,
};

export default PrimaryNav;
